import create from "zustand";
import 'firebase/compat/firestore';

import { ArtistId } from "../../models/Artist";
import { ArtistRef } from "../../models/Directory";
import { GroupId } from "../../models/Group";
import { FreshSong, SongId, MediaReference } from "../../models/Song";
import { OwnerId } from "../../models/User";
import {  serverTimestamp } from "firebase/firestore";

import { SongService } from "../../services/SongService";
import { TransmissionService } from "../../services/TransmissionService";
import { ArtistService } from "../../services/ArtistService";
import { RegionId } from "../../models/Region";
import { ENABLE_GROUPS } from "../../config/env";

export type Transmission = {
  title: string;
  description: string;
  regions: RegionId[];
  media: MediaReference;
  collection: "songs" | "avatars" | "wall" | "message" | "art";
  authors: ArtistId[];
  recentUpload?: SongId;
};

export type TransmissionState = {
  todaysTransmission?: SongId | null;
  title: string;
  assignment: string;
  description: string;
  groups?: GroupId[];
  /** The song's privacy tier. Seeded by the transmit form from the selected
   *  group's defaultPrivacy (student can override); when left undefined,
   *  SongService.transmit falls back to the artist profile's privacy and
   *  then the REACT_APP_DEFAULT_SONG_PRIVACY instance default. */
  privacy?: "public" | "member";
  regions: RegionId[];
  media?: MediaReference;
  art?: MediaReference;
  status: "fetching" | "transmitting" | "idle";
  collection?: "songs" | "avatars" | "wall" | "message" | "art";
  authors?: ArtistId[];
  artists?: Record<ArtistId, ArtistRef>;
  isValid: () => boolean;
  fetchTodaysTransmission: (id: OwnerId) => void;
  setAuthors: (ids: ArtistId[]) => void;
  setGroups: (groups: GroupId[]) => void;
  setPrivacy: (privacy?: "public" | "member") => void;
  setMedia: (media: MediaReference) => void;
  setArt: (art: MediaReference) => void;
  setDescription: (description: string) => void;
  setTitle: (title: string) => void;
  setAssignment: (assignment: string) => void;
  transmit: (ownerId: string) => void;
  clearTodayTransmission: () => void;
  /** Wipes the whole in-progress draft (title, media, selected authors/
   *  groups, todaysTransmission, everything) back to its just-loaded
   *  defaults. This is a single global store, not scoped per signed-in
   *  user, so without this a shared/forgotten-logout device could carry
   *  one student's draft or "today's transmission" into the next
   *  student's session. Called from useProfile's logOut. */
  resetForLogout: () => void;
  revoke: (id: SongId) => void;
  aggregate: (ownerId: string) => FreshSong;
};
export const useTransmission = create<TransmissionState>((set, get) => ({
  status: "idle",
  title: " ",
  regions:[],
  groups: [],
  assignment: "",
  description: " ",
  revoke: async (id) => {
    await SongService.delete(id);
    set({
      todaysTransmission: null,
      status: "idle",
      title: "",
      description: "",
    });
  },
  resetForLogout: () => {
    set({
      todaysTransmission: undefined,
      title: " ",
      assignment: "",
      description: " ",
      groups: [],
      regions: [],
      privacy: undefined,
      media: undefined,
      art: undefined,
      collection: undefined,
      authors: undefined,
      artists: undefined,
      status: "idle",
    });
  },
  clearTodayTransmission: () => {
    set({
      todaysTransmission: null,
      status: "idle",
      title: "",
      description: "",
    });
  },
  isValid: () => {
    const { title, media, groups } = get();
    if (!media) {
      return false;
    }

    const baseValid = (
      title.length > 0 &&
      media.src.length > 0 &&
      media.type.length > 0
    );

    // When groups are disabled (SADFAM), don't require groups
    // When groups are enabled (School of Song), require at least one group
    return ENABLE_GROUPS ? baseValid && (groups?.length || 0) > 0 : baseValid;
  },
  fetchTodaysTransmission: async (id) => {
    set({ status: "fetching" });
    const todaysTransmission =
      await TransmissionService.FetchTodaysTransmission(id);
    set({ todaysTransmission, status: "idle" });
  },
  setMedia: (media: MediaReference) => {
    set({ media });
  },
  setArt: (art: MediaReference) => {
    set({ art });
  },
  setAssignment: (assignment: string) => {
    set({ assignment });
  },
  setGroups: (groups: GroupId[]) => {
    set({ groups });
  },
  setPrivacy: (privacy) => {
    set({ privacy });
  },
  setDescription: (description: string) => {
    set({ description });
  },
  setTitle: (title: string) => {
    set({ title });
  },
  setAuthors: async (ids) => {
    set({ authors: ids });
    const artists=await Promise.all(ids.map(id=>{
      return ArtistService.fetch(id)
    }))
    const regions:any=artists.map(x=>x.region).filter(x=> typeof x!=='undefined');
    set({regions});
  },
  aggregate: (ownerId) => {
    const { authors, title, media, description, groups, assignment, privacy } = get();
    const freshSong = {
      authors,
      media,
      description,
      ownerId,
      groups,
      assignment,
      title,
      updatedAt: serverTimestamp(),
    } as FreshSong;
    // Scalar mirror of groups[0] — the privacy rules and every group feed
    // query key off this field.
    if (groups && groups.length > 0) freshSong.groupId = groups[0];
    // Firestore rejects explicit `undefined` field values, so only include
    // privacy when a tier was actually chosen/seeded.
    if (privacy) freshSong.privacy = privacy;
    return freshSong;
  },
  transmit: async (ownerId) => {
    const state = get();
    let transmission = state.aggregate(ownerId);
    const artists=await Promise.all(transmission.authors.map(id=>{
      return ArtistService.fetch(id)
    }))
    const regions = artists.map(x=>x.region).filter(x=>typeof x!='undefined');
    transmission.regions= regions as any;
    if (state.art) transmission.art = state.art;
    if (state.assignment) transmission.assignment = state.assignment;
    set({ status: "transmitting" });
    try {
      const song = await SongService.transmit(transmission);
      set({ todaysTransmission: song.id, status: "idle" });
    } catch (e) {
      set({ status: "idle" });
      throw e;
    }
  },
}));
